import { Link } from "react-router-dom";
import { ExternalLink, Maximize2 } from "lucide-react";
import {
  StepScreenshot,
  type TargetBox,
} from "@/components/walkthrough/step-screenshot";
import { PersonaVoice } from "@/components/ui/persona-voice";
import { Timestamp } from "@/components/ui/timestamp";
import type { QueueRowData } from "./queue-types";

export interface QueueDetailEvidenceProps {
  projectId: string;
  detail: unknown;
  row: QueueRowData;
  observations: Array<{
    id: string;
    walkthroughId: string;
    stepNumber?: number | null;
    stepTitle?: string | null;
    personaReasoning?: string | null;
    surface?: string | null;
    pageUrl?: string | null;
    severity: string | null;
    createdAt: string;
    targetBox?: TargetBox | null;
  }>;
  onOpenZen?: () => void;
}

export function QueueDetailEvidence({
  projectId,
  detail,
  row,
  observations,
  onOpenZen,
}: QueueDetailEvidenceProps) {
  if (observations.length === 0 && typeof row.stepNumber !== "number") {
    return null;
  }

  const canonicalData = detail as {
    effectiveTargetBox?: TargetBox | null;
    effectiveStepNumber?: number | null;
    effectiveWalkthroughId?: string | null;
  } | null;

  const latestWithStep = observations.find(
    (o) => typeof o.stepNumber === "number",
  );

  const stepNum =
    canonicalData?.effectiveStepNumber ??
    latestWithStep?.stepNumber ??
    row.stepNumber;
  const wtId =
    canonicalData?.effectiveWalkthroughId ??
    latestWithStep?.walkthroughId ??
    row.walkthroughId;
  const tb =
    canonicalData?.effectiveTargetBox ??
    latestWithStep?.targetBox ??
    (row.targetBox as TargetBox | null);

  if (typeof stepNum !== "number" || !wtId) {
    return null;
  }

  const matchingObs =
    observations.find(
      (o) => o.walkthroughId === wtId && o.stepNumber === stepNum,
    ) ?? latestWithStep;

  const pageUrl = matchingObs?.pageUrl ?? row.pageUrl;
  const stepTitle = matchingObs?.stepTitle ?? null;
  const personaReasoning = matchingObs?.personaReasoning ?? null;

  const highlights = tb
    ? [
        {
          id: "queue-target",
          box: tb,
          tone: row.severity || row.type,
          label: row.title ?? (row.type === "helps" ? "Strength" : "Issue"),
          isActive: true,
        },
      ]
    : undefined;

  return (
    <section
      className="space-y-2 border-t border-border pt-4"
      aria-label="Visual evidence"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">
          Visual evidence
        </h3>
        {observations.length > 1 && (
          <span className="text-xs text-muted-foreground">
            {observations.length} observations
          </span>
        )}
      </div>

      {personaReasoning && (
        <div className="rounded-lg border border-border bg-surface-tonal/60 p-3.5 space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">
              {row.personaName || "Persona"}
            </span>
            {stepTitle && <span className="truncate">{stepTitle}</span>}
          </div>
          <PersonaVoice size="sm">
            “
            {personaReasoning
              .replace(/^[A-Za-zÀ-ÖØ-öø-ÿ]+\s*:\s*/, "")
              .replace(/^["“”]+|["“”]+$/g, "")}
            ”
          </PersonaVoice>
        </div>
      )}

      <figure className="space-y-1.5">
        <div className="overflow-hidden rounded-md border border-border bg-card shadow-xs">
          <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-border" />
              <span className="size-2 rounded-full bg-border" />
              <span className="size-2 rounded-full bg-border" />
              <span className="ml-2 font-mono text-xs text-muted-foreground">
                Step {stepNum}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {onOpenZen && (
                <button
                  type="button"
                  onClick={onOpenZen}
                  className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline cursor-pointer"
                  title="Inspect in Zen Mode Carousel"
                >
                  <Maximize2 className="size-3 text-primary" />
                  <span>Zen Mode</span>
                </button>
              )}
              <Link
                to={`/projects/${projectId}/walkthroughs/${wtId}?step=${stepNum}`}
                className="inline-flex items-center gap-1 text-xs hover:text-foreground transition-colors"
              >
                <span>View step</span>
                <ExternalLink className="size-3" aria-hidden="true" />
              </Link>
            </div>
          </div>
          <div className="flex max-h-80 w-full items-center justify-center overflow-hidden bg-black/5 dark:bg-black/30">
            <StepScreenshot
              walkthroughId={wtId}
              stepNumber={stepNum}
              targetBox={tb}
              targetTone={row.severity || row.type}
              highlights={highlights}
              className="max-h-80 w-full"
              alt={`Step ${stepNum} of the walkthrough, where this was seen`}
              onOpenZoom={onOpenZen ? () => onOpenZen() : undefined}
            />
          </div>
          {pageUrl && (
            <div className="flex items-center justify-between gap-2 border-t border-border/40 px-3 py-1.5 text-xs">
              <span className="text-muted-foreground shrink-0">
                Captured URL:
              </span>
              <a
                href={pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 truncate font-mono text-xs text-foreground hover:underline"
                title={pageUrl}
              >
                <span className="truncate">{pageUrl}</span>
                <ExternalLink className="size-3 shrink-0 text-muted-foreground" />
              </a>
            </div>
          )}
        </div>
      </figure>

      {observations.length > 1 && (
        <ul className="space-y-1 pt-1">
          {observations.map((o) => (
            <li key={o.id}>
              <Link
                className="text-sm underline underline-offset-2"
                to={`/projects/${projectId}/walkthroughs/${o.walkthroughId}${
                  o.stepNumber ? `?step=${o.stepNumber}` : ""
                }`}
              >
                {o.stepNumber ? `Step ${o.stepNumber}` : "Walkthrough"}
              </Link>{" "}
              <span className="text-xs text-muted-foreground">
                <Timestamp date={o.createdAt} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
